import React, { useEffect, useState } from 'react';

import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { setSelectedMerchants } from '@/redux/ducks/userConfig/action';
import { Button, InlineSearchSelect, Modal } from '@/widgets';
import { BusinessIcon, CrossIcon } from '@/assets/icons';
import { RootState } from '@/redux/ducks';
import {
	TEST_IDS,
	MERCHANT_SELECT_MODAL_OPENED,
	MERCHANT_SELECT_MODAL_CLOSED,
	MERCHANT_SELECT_MODAL_APPLY_CLICKED,
} from '@/constants';
import { sendClickEvent } from '@/utils/AnalyticsUtils';

import styles from './MerchantListSelect.module.scss';
import { IMerchantListSelectProps, IMerchantMeta } from './merchantList.types';

export const morphMerchantList = (merchantList: IMerchantMeta[]) => {
	const morphedFilters = merchantList.map((merchantItem) => {
		return {
			key: merchantItem.merchantId,
			title: merchantItem.displayName,
			subtitle: merchantItem.merchantId,
			value: merchantItem.merchantId,
			type: merchantItem.type,
			isDisabled: () => false,
		};
	});
	return morphedFilters;
};

const MerchantListSelect = (props: IMerchantListSelectProps) => {
	const { mode = 'multiple', loading = false, routeName } = props;
	const dispatch = useDispatch();
	const { merchantList = [] } = useSelector(
		(state: RootState) => state.merchantProfile,
	);
	const { selectedMerchants = [] } = useSelector(
		(state: RootState) => state.userConfig[routeName],
	);
	const [selectedValues, setSelectedValues] = useState([]);
	const [showMerchantSelectModal, setShowMerchantSelectModal] = useState(false);

	useEffect(() => {
		setSelectedValues([...selectedMerchants]);
	}, [selectedMerchants]);

	const shouldMultiSelect = mode !== 'single';
	const shouldAllOrSingleSelect = mode === 'allOrSingle';

	// Returning from here if merchant list in not present
	if (isEmpty(merchantList)) return null;

	const handleCancelClick = () => {
		setSelectedValues([...selectedMerchants]);
		setShowMerchantSelectModal(false);
		sendClickEvent({ name: MERCHANT_SELECT_MODAL_CLOSED });
	};

	const onApplyClick = () => {
		dispatch(
			setSelectedMerchants({ routeName, selectedMerchants: selectedValues }),
		);
		setShowMerchantSelectModal(false);
		sendClickEvent({
			name: MERCHANT_SELECT_MODAL_APPLY_CLICKED,
		});
	};

	const handleMerchantSelectModalOpen = () => {
		setShowMerchantSelectModal(true);
		sendClickEvent({ name: MERCHANT_SELECT_MODAL_OPENED });
	};
	const modalTitle = () => (
		<div className={styles.modalTitle}>
			Select Merchants
			<Button
				onClick={handleCancelClick}
				variant="text"
				className={styles.crossIcon}
			>
				<CrossIcon />
			</Button>
		</div>
	);

	const modalFooter = () => (
		<div className={styles.modalFooter}>
			<Button
				onClick={handleCancelClick}
				className={styles.cancelButton}
				variant="tertiary"
				data-id={TEST_IDS.MERCHANT_LIST.CANCEL_MODAL_BTN}
			>
				Cancel
			</Button>
			<Button
				data-id={TEST_IDS.MERCHANT_LIST.APPLY_MERCHANTS_BTN}
				onClick={onApplyClick}
				className={styles.applyButton}
				variant="primary"
				disabled={isEmpty(selectedValues)}
			>
				Apply
			</Button>
		</div>
	);

	return (
		<>
			<Button
				disabled={loading}
				variant="tertiary"
				data-id={TEST_IDS.MERCHANT_LIST.OPEN_MODAL_BTN}
				className={styles.selectedMerchantButton}
				onClick={() => handleMerchantSelectModalOpen()}
			>
				<BusinessIcon /> Select Merchants{' '}
				{selectedMerchants.length > 0 ? (
					<span className={styles.selectedMerchantCount}>
						{selectedMerchants.length}
					</span>
				) : null}
			</Button>
			<Modal
				className={styles.modal}
				footer={modalFooter()}
				open={showMerchantSelectModal}
				title={modalTitle()}
				maskClosable
				destroyOnClose
				closable={false}
				onCancel={handleCancelClick}
				data-id={TEST_IDS.MERCHANT_LIST.MODAL}
			>
				{/* @ts-expect-error - NOTE: will be picked as a separate task */}
				<InlineSearchSelect
					//@ts-expect-error - NOTE: will be picked as a refactor
					isMultiSelect={shouldMultiSelect}
					showSelectAll={shouldMultiSelect}
					allOrSingle={shouldAllOrSingleSelect}
					classes={{
						inputContainer: styles.searchInput,
						listWrapper: styles.listWrapper,
					}}
					inputProps={{ bordered: false }}
					setSelectedOptions={setSelectedValues}
					options={morphMerchantList(merchantList)}
					selectedOptions={selectedValues}
				/>
			</Modal>
		</>
	);
};

export default MerchantListSelect;
